/**
 * Sized sources for CMS media. Umbraco serves /media/ through ImageSharp,
 * which resizes on `?width=` / `?height=` and re-encodes on `?format=webp`.
 *
 * Editors upload originals far larger than their box (a 1562px logo shown
 * 162px wide, a 1280px photo in a 546px card), and only a processed response
 * carries a Cache-Control (7 days, immutable with the `?v=` the API adds): the
 * original file is served with none, so every visit downloaded it again.
 *
 * Only raster files the processor understands are rewritten. An SVG, a GIF, a
 * video, a URL outside /media/ or one that already asks for a size (an editor
 * crop) comes back unchanged.
 */

import type { ImgHTMLAttributes } from "react";

/**
 * The widths a srcset offers. A fixed ladder rather than per-box widths keeps
 * the server's processed-image cache small and makes responses shareable
 * between components.
 *
 * It stops at 1920 because the processor upscales: asked for more than the
 * original has, it returns a bigger file of the same picture. The API reports
 * 0x0 for media today, so the original's width is usually unknown and cannot
 * cap the ladder itself.
 */
const SRCSET_WIDTHS = [320, 480, 640, 800, 1080, 1280, 1600, 1920];

/** The `src` fallback for a browser that ignores srcset. */
const FALLBACK_WIDTH = 1280;

/**
 * Asked for WebP alone, the processor keeps a PNG original lossless: a photo
 * uploaded as PNG came back at 2.4 MB for 1280px, three times the original.
 * A quality makes it encode lossy (keeping transparency): 227 KB.
 */
const WEBP_QUALITY = 80;

const PROCESSABLE = /\.(?:jpe?g|png|webp)$/i;

export interface CmsImageSources {
  src: string;
  srcSet?: string;
  sizes?: string;
}

/**
 * Spread onto the one image most likely to be the page's LCP element (a hero,
 * a product's main image), so it is fetched ahead of the other images. React
 * 18 does not know the `fetchPriority` prop (React 19 does) and would warn, so
 * the attribute goes through under its HTML name.
 */
export const LCP_IMAGE_PRIORITY = {
  fetchpriority: "high",
} as unknown as ImgHTMLAttributes<HTMLImageElement>;

function processableUrl(url: string): URL | null {
  try {
    const parsed = new URL(url, window.location.origin);
    if (!parsed.pathname.startsWith("/media/")) return null;
    if (!PROCESSABLE.test(parsed.pathname)) return null;
    if (parsed.searchParams.has("width") || parsed.searchParams.has("height")) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function withSize(base: URL, size: { width?: number; height?: number }) {
  const sized = new URL(base);
  if (size.width) sized.searchParams.set("width", String(size.width));
  if (size.height) sized.searchParams.set("height", String(size.height));
  sized.searchParams.set("format", "webp");
  sized.searchParams.set("quality", String(WEBP_QUALITY));
  return sized.toString();
}

/**
 * The URL of a CMS image at one width, as WebP. Unprocessable URLs come back
 * as given.
 */
export function cmsImageUrl(url: string, width: number): string {
  const base = processableUrl(url);
  return base ? withSize(base, { width: Math.round(width) }) : url;
}

/**
 * Sources for an image whose rendered width follows the layout (a hero, a
 * card, a gallery image): a width srcset the browser picks from using `sizes`
 * and the screen's pixel density.
 *
 * @param sizes - The `sizes` attribute: the image's rendered CSS width, e.g.
 *   "(max-width: 768px) 100vw, 50vw".
 * @param intrinsicWidth - The original's width, when the CMS measured it. Caps
 *   the ladder so nothing is upscaled; the original's own width is then the
 *   largest candidate.
 */
export function responsiveCmsImage(
  url: string,
  sizes: string,
  intrinsicWidth?: number | null
): CmsImageSources {
  const base = processableUrl(url);
  if (!base) return { src: url };

  const cap = intrinsicWidth && intrinsicWidth > 0 ? intrinsicWidth : null;
  const widths = cap
    ? [...SRCSET_WIDTHS.filter((width) => width < cap), cap]
    : SRCSET_WIDTHS;
  const fallback = cap ? Math.min(cap, FALLBACK_WIDTH) : FALLBACK_WIDTH;

  return {
    src: withSize(base, { width: fallback }),
    srcSet: widths
      .map((width) => `${withSize(base, { width })} ${width}w`)
      .join(", "),
    sizes,
  };
}

/**
 * Sources for an image drawn at a fixed CSS size (a logo, an icon, a
 * thumbnail): 1x, 2x and 3x candidates for the screen's pixel density.
 *
 * Give the side CSS fixes: a logo set to `height: 32px; width: auto` passes
 * `{ height: 32 }`, and the server keeps the aspect ratio.
 */
export function fixedCmsImage(
  url: string,
  box: { width: number } | { height: number }
): CmsImageSources {
  const base = processableUrl(url);
  if (!base) return { src: url };

  const at = (density: number) =>
    "width" in box
      ? withSize(base, { width: Math.round(box.width * density) })
      : withSize(base, { height: Math.round(box.height * density) });

  return {
    src: at(2),
    srcSet: [1, 2, 3].map((density) => `${at(density)} ${density}x`).join(", "),
  };
}
